import { useEffect, useState } from "react";
import { Check, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const LABELS = { videos: "Videos", exercises: "Exercises", flash_cards: "Flash cards", stories: "Stories", kiko_stars: "Kiko & stars",
  yes: "Yes", a_bit_high: "A bit high", too_high: "Too high" };

function Stories() {
  const [list, setList] = useState(null);
  const load = () => api.get("/reading-admin/stories").then(({ data }) => setList(data.stories));
  useEffect(() => { load(); }, []);
  const set = async (id, approved) => {
    await api.post(`/reading-admin/stories/${id}`, { approved });
    toast.success(approved ? "Story published to children" : "Story hidden");
    load();
  };
  if (!list) return null;
  return (
    <div className="space-y-4" data-testid="admin-reading-stories">
      <p className="text-sm text-slate-400">Children only see a story after you approve it, and once they have mastered its lesson.</p>
      {list.map((s) => (
        <div key={s.id} className="rounded-2xl border-2 border-slate-700 p-4" data-testid={`admin-story-${s.id}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-bold text-lg flex-1">{s.lang.toUpperCase()} · {s.title} <span className="text-sm font-normal text-slate-400">unlocks after {s.unlock}</span></h3>
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${s.approved ? "bg-green-300 text-slate-900" : "bg-slate-700"}`}>{s.approved ? "Published" : "Waiting for review"}</span>
            <button onClick={() => set(s.id, !s.approved)} data-testid={`admin-story-toggle-${s.id}`}
              className={`rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1 ${s.approved ? "border-2 border-slate-500" : "bg-green-400 text-slate-900"}`}>
              {s.approved ? <><EyeOff size={14} /> Hide</> : <><Check size={14} /> Approve</>}
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-3">
            {s.pages.map((pg, i) => (
              <div key={i} className="rounded-xl bg-white text-slate-900 p-2 text-center">
                <img src={`/reading/pics/${s.pics[i]}.webp`} alt="" className="w-full aspect-square object-contain" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                <p className="text-sm font-bold mt-1">{pg.text}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Surveys() {
  const [d, setD] = useState(null);
  const [cands, setCands] = useState([]);
  const loadCands = () => api.get("/reading-admin/survey-candidates").then(({ data }) => setCands(data.parents)).catch(() => {});
  useEffect(() => { api.get("/reading-admin/surveys").then(({ data }) => setD(data)); loadCands(); }, []);
  const send = async (pid) => {
    try {
      await api.post(`/reading-admin/surveys/${pid}/send`);
      toast.success("Survey email sent");
      loadCands();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    }
  };
  if (!d) return null;
  return (
    <div className="space-y-3" data-testid="admin-reading-surveys">
      <h3 className="font-bold text-lg">Parents using Reading ({cands.length})</h3>
      {cands.map((c) => (
        <div key={c.parent_id} className="rounded-xl border-2 border-slate-700 px-4 py-3 text-sm flex items-center gap-3 flex-wrap" data-testid={`admin-rsurvey-cand-${c.parent_id}`}>
          <span className="flex-1"><b>{c.name}</b> · {c.email} · {c.children.join(", ")} · started {c.first_at ? new Date(c.first_at).toLocaleDateString() : "—"}</span>
          {c.done ? <span className="text-green-300 font-bold">Answered</span> : (
            <button onClick={() => send(c.parent_id)} data-testid={`admin-rsurvey-send-${c.parent_id}`}
              className="rounded-full bg-green-400 text-slate-900 px-3 py-1.5 font-bold">{c.sent ? "Send again" : "Send survey"}</button>
          )}
        </div>
      ))}
      <h3 className="font-bold text-lg">Pilot survey answers ({d.surveys.length})</h3>
      {d.surveys.length === 0 && <p className="text-slate-400 text-sm">No answers yet. Parents get the survey by email 14 days after their child starts Reading.</p>}
      {d.surveys.map((s, k) => (
        <div key={k} className="rounded-2xl border-2 border-slate-700 p-4 text-sm" data-testid={`admin-reading-survey-${k}`}>
          <p className="font-bold">{s.name} · <span className="font-normal text-slate-400">{s.email} · {new Date(s.at).toLocaleDateString()}</span></p>
          <ul className="mt-2 space-y-1">
            {d.questions.map((q) => <li key={q.id}><span className="text-slate-400">{q.en}</span> — <b>{q.type === "scale" ? `${s.answers[q.id]}/5` : LABELS[s.answers[q.id]] || s.answers[q.id] || "—"}</b></li>)}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function ReadingAdmin() {
  const [tab, setTab] = useState("stories");
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {[["stories", "Kiko stories"], ["surveys", "Pilot survey"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-reading-tab-${k}`}
            className={`px-4 py-2 rounded-full border-2 text-sm font-semibold inline-flex items-center gap-1 ${tab === k ? "bg-white text-slate-900" : "border-slate-600"}`}>
            <Eye size={14} /> {l}
          </button>
        ))}
      </div>
      {tab === "stories" ? <Stories /> : <Surveys />}
    </div>
  );
}
